/**
 * Storage Node Protocol v1 — Self-Describing Encrypted Envelope
 * Specified in docs/STORAGE_NODE_PROTOCOL_V1.md §5 & §12
 */

export const ENVELOPE_MAGIC = new Uint8Array([0x59, 0x41, 0x44, 0x41]); // "YADA"
/** v1: header is not authenticated and names no context. */
export const ENVELOPE_VERSION = 1;
/** v2: the encoded prefix (magic, version, header) is the AES-GCM additional data, and `ctx` names the workspace and object. */
export const ENVELOPE_VERSION_BOUND = 2;
export const SUPPORTED_ENVELOPE_VERSIONS: readonly number[] = [ENVELOPE_VERSION, ENVELOPE_VERSION_BOUND];

export type EnvelopeKeyScheme =
  | { scheme: 'epoch'; epoch: number }
  | { scheme: 'wrapped-dek'; kek_id: string; wrapped_dek: string };

export type EnvelopeHeaderKind = 'snapshot' | 'update' | 'asset';

export interface EnvelopeHeader {
  kind: EnvelopeHeaderKind;
  alg: 'A256GCM';
  key: EnvelopeKeyScheme;
  iv: string; // Base64 IV
  mime_type?: string;
  doc_type?: string;
  content?: string;
  /** v2 only: the workspace and object (document id; absent for content-addressed assets) the ciphertext belongs to. */
  ctx?: { ws: string; id?: string };
}

export interface EncryptedEnvelopePayload {
  version: number;
  header: EnvelopeHeader;
  ciphertext: Uint8Array;
}

export function isEnvelope(blob: Uint8Array): boolean {
  if (blob.byteLength < 9) return false;
  return (
    blob[0] === 0x59 &&
    blob[1] === 0x41 &&
    blob[2] === 0x44 &&
    blob[3] === 0x41 &&
    SUPPORTED_ENVELOPE_VERSIONS.includes(blob[4])
  );
}

/**
 * Encodes an envelope header and ciphertext into a raw binary blob.
 */
export function encodeEnvelope(
  header: EnvelopeHeader,
  ciphertext: Uint8Array,
  version: number = ENVELOPE_VERSION
): Uint8Array {
  const jsonHeader = JSON.stringify(header);
  const headerBytes = new TextEncoder().encode(jsonHeader);
  const headerLength = headerBytes.byteLength;

  const totalLength = 4 + 1 + 4 + headerLength + ciphertext.byteLength;
  const result = new Uint8Array(totalLength);

  // 1. Magic "YADA" (4 bytes)
  result.set(ENVELOPE_MAGIC, 0);

  // 2. Version (1 byte)
  result[4] = version;

  // 3. Header Length (4 bytes, Big-Endian)
  const view = new DataView(result.buffer, result.byteOffset, result.byteLength);
  view.setUint32(5, headerLength, false);

  // 4. Header JSON (headerLength bytes)
  result.set(headerBytes, 9);

  // 5. Ciphertext
  result.set(ciphertext, 9 + headerLength);

  return result;
}

/**
 * Decodes a raw binary blob into an envelope header and ciphertext.
 */
export function decodeEnvelope(blob: Uint8Array): EncryptedEnvelopePayload {
  if (blob.byteLength < 9) {
    throw new Error('Invalid envelope: blob too short');
  }

  if (
    blob[0] !== 0x59 ||
    blob[1] !== 0x41 ||
    blob[2] !== 0x44 ||
    blob[3] !== 0x41
  ) {
    throw new Error('Invalid envelope: missing YADA magic bytes');
  }

  const version = blob[4];
  if (!SUPPORTED_ENVELOPE_VERSIONS.includes(version)) {
    throw new Error(`Unsupported envelope version: ${version}`);
  }

  const view = new DataView(blob.buffer, blob.byteOffset, blob.byteLength);
  const headerLength = view.getUint32(5, false);

  if (blob.byteLength < 9 + headerLength) {
    throw new Error('Invalid envelope: corrupted header length');
  }

  const headerBytes = blob.subarray(9, 9 + headerLength);
  const jsonStr = new TextDecoder().decode(headerBytes);
  const header = JSON.parse(jsonStr) as EnvelopeHeader;

  const ciphertext = blob.subarray(9 + headerLength);

  return {
    version,
    header,
    ciphertext,
  };
}
